/**
 * ENG-4911 - pure helper for the integration-change hash used by the
 * `/host/refresh` reconciliation loop in manager-worker.
 *
 * Originally the hash covered only `definition_id + credentials`, which
 * meant a config-only change in the database (e.g. switching
 * `xero_tenant_id` from one tenant to another without rotating the
 * access token) silently failed to propagate: the hash matched the
 * stored value, the manager short-circuited before `writeIntegrations`,
 * `.env.integrations` was never rewritten, and the running MCP child
 * kept calling Xero with the old tenant id. This was a real
 * cross-tenant data exposure path on Sterling 2026-05-10.
 *
 * The fix is to also fold `config` into the hash payload. We use a
 * stable, ordered representation (sorted keys) so that two semantically
 * equivalent config blobs hash identically - without that, JSON's
 * insertion-order serialization could spuriously bump the hash on every
 * poll.
 *
 * ENG-7946: `computeCapabilitySurfaceHash` is a narrower hash covering
 * only the fields that determine the agent's callable tool surface
 * (definition_id, display_name, capabilities). The `integration-update`
 * notice is gated on THIS hash, not the full credential+config hash, so
 * token refreshes (which bump `knownIntegrationHashes` via the
 * `credentials` field) do not fire a spurious notice at the agent.
 */

import { createHash } from 'node:crypto';
import { isControlPlaneOnlyConfigKey } from '@augmented/core';

interface HashableIntegration {
  definition_id: string;
  credentials?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
}

/**
 * ENG-7946: the capability surface of a single integration - the subset of
 * fields that define what the agent can actually DO with it.
 * - `definition_id`: which integration (e.g. "github", "xero")
 * - `display_name`: name rendered in the `integration-update` notice
 * - `capabilities`: the access-gated capability set (id + access level)
 *
 * Deliberately excludes `credentials` and `config` so that a token refresh
 * or a config-only write (e.g. Anchor session-id mint) does not change this
 * hash and therefore does not fire a spurious `integration-update` notice.
 */
export interface CapabilitySurfaceIntegration {
  definition_id: string;
  display_name?: string | null;
  capabilities?: Array<{ id: string; access: string }> | null;
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      out[key] = canonicalize(obj[key]);
    }
    return out;
  }
  return value;
}

/**
 * ENG-6918 / ENG-8690 follow-up: drop control-plane-only bookkeeping from the
 * config before hashing.
 *
 * `managed_state_token` is a one-shot OAuth CSRF token re-minted on a 15-minute
 * TTL, and it lives in the same `config` blob this hash folds in verbatim. So a
 * token the agent never sees, and which no longer reaches `.env.integrations`
 * at all, still moved the hash on the very next poll — dragging the whole
 * reload block (writeIntegrations, the env diff, the MCP reap consideration)
 * through a full pass every ~15 min per managed integration, fleet-wide.
 *
 * It can no longer produce a RESTART: ENG-6918 filters these names out of
 * `respawnVars`, and `needsRespawn` requires that list to be non-empty. This is
 * the layer above — it stops the poll from doing the work at all, and removes
 * the last path by which a future writer could reconnect a re-mint to the
 * provisioning tally that auto-pauses an agent.
 *
 * Deliberately reuses `isControlPlaneOnlyConfigKey` rather than re-listing the
 * keys. Two subsystems already share that predicate precisely so a third cannot
 * drift from it.
 */
function hashableConfig(config: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!config) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config)) {
    if (isControlPlaneOnlyConfigKey(key)) continue;
    out[key] = value;
  }
  return out;
}

export function computeIntegrationsHash(integrations: HashableIntegration[]): string {
  const payload = integrations
    .map((i) =>
      `${i.definition_id}:${JSON.stringify(canonicalize(i.credentials ?? {}))}:${JSON.stringify(canonicalize(hashableConfig(i.config)))}`,
    )
    // ENG-8003: sort so the hash is invariant to the ARRAY order too, not just
    // intra-object key order. The /host/refresh integration list is not
    // order-stable (observed on agt-aws-1 as a cyclic rotation of the same set
    // on nearly every poll). Without this sort, each reorder produced a
    // different hash, read as a phantom "integration changed", which triggered
    // a session respawn every poll. That respawn storm repeatedly SIGINT-killed
    // the healthy slack-channel MCP until the mcp-presence-reaper quarantined
    // it (ENG-5932) and the agent went mute on Slack. The sort key is each
    // entry's full serialized string (definition_id:credentials:config), so the
    // ordering is deterministic even when two connections share a definition_id
    // (ENG-7543); it extends canonicalize()'s "order never registers as a
    // change" invariant from object keys to the membership array.
    .sort();
  return createHash('sha256')
    .update(JSON.stringify(payload))
    .digest('hex')
    .slice(0, 16);
}

/**
 * ENG-7946: hash the capability surface - definition_id + display_name +
 * capabilities sorted by id. Only changes when the agent's callable tool
 * surface changes (integration added/removed, capability reconfigured).
 * Token refreshes and config-only writes leave this hash unchanged.
 *
 * Integrations are sorted by definition_id before hashing so the result
 * is order-independent: the same set of integrations returned in a different
 * order produces the same hash and does not trigger a spurious notice.
 */
export function computeCapabilitySurfaceHash(integrations: CapabilitySurfaceIntegration[]): string {
  const payload = [...integrations]
    .sort((a, b) => a.definition_id.localeCompare(b.definition_id))
    .map((i) => {
      const caps = [...(i.capabilities ?? [])]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((c) => `${c.id}:${c.access}`);
      return `${i.definition_id}:${i.display_name ?? ''}:${JSON.stringify(caps)}`;
    });
  return createHash('sha256')
    .update(JSON.stringify(payload))
    .digest('hex')
    .slice(0, 16);
}
